import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Phone, Plus, Search, Bookmark, Home, Pencil, Trash2, X, Save, Square, CheckSquare, ArrowUpDown } from "lucide-react";
import http from "@/lib/http";
import { t } from "@/i18n";
import { formatEuro, contractProgress, daysSince, parseDate } from "@/lib/format";
import QuickCallDialog from "@/components/QuickCallDialog";
import LiveStatusBadge from "@/components/LiveStatusBadge";
import StatusChip from "@/components/StatusChip";
import { formatTimeSince } from "@/lib/format";
import ConfirmDialog from "@/components/ConfirmDialog";
import LeadBulkBar, { leadDeleteMessage } from "@/components/LeadBulkBar";
import BulkArchiveDialog from "@/components/BulkArchiveDialog";
import LeadFilterChips, { readFilterFromParams, writeFilterToParams, filterToApiParams } from "@/components/LeadFilterChips";
import { useLeadSelection } from "@/lib/useLeadSelection";

export default function Particulieren() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const groupId = params.get("group") || "all";
  const [leads, setLeads] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [callLead, setCallLead] = useState(null);
  const [query, setQuery] = useState("");
  const [editingGroup, setEditingGroup] = useState(null); // null | "new" | group obj
  const [groupForm, setGroupForm] = useState({ name: "", postcodes: "" });
  // Sortering — persistente keuze via URL-param zodat refresh hem bewaart.
  const sortKey = params.get("sort") || "date_posted_desc";
  const setSort = (s) => {
    const n = new URLSearchParams(params);
    if (s === "date_posted_desc") n.delete("sort"); else n.set("sort", s);
    setParams(n);
  };
  const filter = readFilterFromParams(params);
  const setFilter = (next) => setParams(writeFilterToParams(next, params));

  const refreshGroups = async () => {
    const r = await http.get("/api/postcode-groups");
    setGroups(r.data);
  };
  // Request-token om race conditions te voorkomen wanneer meerdere fetches
  // tegelijkertijd onderweg zijn (bv. eerste vuur zonder groups, tweede met
  // groups). Enkel de meest recente respons mag de UI-state overschrijven.
  const reqIdRef = useRef(0);

  const refreshLeads = async (gid) => {
    const myId = ++reqIdRef.current;
    setLoading(true);
    const params = { source_type: "particulier", ...filterToApiParams(filter) };
    const activeGroup = (gid && gid !== "all")
      ? groups.find(g => g.id === gid) : null;
    if (activeGroup && activeGroup.postcodes?.length) {
      params.postal_code = activeGroup.postcodes.join(",");
    }
    try {
      const r = await http.get("/api/leads", { params });
      if (myId !== reqIdRef.current) return;  // outdated response, discard
      setLeads(r.data);
    } finally {
      if (myId === reqIdRef.current) setLoading(false);
    }
  };

  // Bij groep-wissel: laat geen oude leads staan terwijl de nieuwe lijst
  // laadt. Voorkomt visueel "alle postcodes worden plots in Ruppelstreek getoond".
  useEffect(() => { setLeads([]); }, [groupId]);

  useEffect(() => { refreshGroups(); }, []);
  useEffect(() => { if (groups.length > 0 || groupId === "all") refreshLeads(groupId); /* eslint-disable-next-line */ },
    [groupId, groups, params.get("f_status"), params.get("f_live"), params.get("f_option"), params.get("f_pmin"), params.get("f_pmax"), params.get("f_action")]);

  const filtered = useMemo(() => {
    // 1) Tekst-filter
    let out = leads;
    if (query.trim()) {
      const q = query.toLowerCase();
      out = out.filter(l =>
        l.address?.toLowerCase().includes(q) ||
        l.postal_code?.includes(q) ||
        l.city?.toLowerCase().includes(q) ||
        l.owner_name?.toLowerCase().includes(q)
      );
    }
    // 2) Sort — nieuw → oud (default), oud → nieuw, of prijs.
    // date_posted is in DD/MM/YYYY (Belgisch). null-waarden gaan altijd naar onderen.
    const arr = [...out];
    const dateRank = (l) => {
      const d = parseDate(l.date_posted);
      return d ? d.getTime() : null;
    };
    if (sortKey === "date_posted_asc") {
      arr.sort((a, b) => {
        const da = dateRank(a), db = dateRank(b);
        if (da === null && db === null) return 0;
        if (da === null) return 1;
        if (db === null) return -1;
        return da - db;
      });
    } else if (sortKey === "price_asc") {
      arr.sort((a, b) => {
        const pa = a.price ?? Number.POSITIVE_INFINITY;
        const pb = b.price ?? Number.POSITIVE_INFINITY;
        return pa - pb;
      });
    } else if (sortKey === "price_desc") {
      arr.sort((a, b) => {
        const pa = a.price ?? Number.NEGATIVE_INFINITY;
        const pb = b.price ?? Number.NEGATIVE_INFINITY;
        return pb - pa;
      });
    } else {
      // default: date_posted_desc — newest first
      arr.sort((a, b) => {
        const da = dateRank(a), db = dateRank(b);
        if (da === null && db === null) return 0;
        if (da === null) return 1;
        if (db === null) return -1;
        return db - da;
      });
    }
    return arr;
  }, [leads, query, sortKey]);

  const setGroup = (gid) => {
    const n = new URLSearchParams(params);
    if (gid === "all") n.delete("group"); else n.set("group", gid);
    setParams(n);
  };

  const openLead = (id) => {
    navigate(`/lead/${id}`, {
      state: { from: location.pathname + location.search, list: filtered.map(l => l.id) },
    });
  };

  const sel = useLeadSelection(() => refreshLeads(groupId));

  const startNewGroup = () => {
    setEditingGroup("new");
    setGroupForm({ name: "", postcodes: "" });
  };
  const startEditGroup = (g) => {
    setEditingGroup(g);
    setGroupForm({ name: g.name, postcodes: (g.postcodes || []).join(", ") });
  };
  const saveGroup = async (e) => {
    e?.preventDefault();
    const codes = groupForm.postcodes.split(",").map(s => s.trim()).filter(Boolean);
    const payload = { name: groupForm.name.trim(), postcodes: codes };
    if (!payload.name) return;
    if (editingGroup === "new") {
      await http.post("/api/postcode-groups", payload);
    } else {
      await http.put(`/api/postcode-groups/${editingGroup.id}`, payload);
    }
    setEditingGroup(null);
    await refreshGroups();
  };
  const deleteGroup = async (g) => {
    if (!window.confirm(t("particulieren.delete_group_confirm", { name: g.name }))) return;
    await http.delete(`/api/postcode-groups/${g.id}`);
    if (groupId === g.id) setGroup("all");
    await refreshGroups();
  };

  return (
    <div className="flex-1 flex min-w-0" data-testid="particulieren-screen">
      <div className="flex-1 min-w-0 p-8 overflow-auto">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[13px] text-[var(--teal-700)] font-medium mb-2 uppercase tracking-[0.14em]">
              {t("nav.pipeline")}
            </div>
            <h1 className="font-display text-[32px] leading-tight text-[var(--ink-900)]">
              {t("particulieren.title")}
            </h1>
            <p className="text-[14px] text-[var(--ink-500)] mt-1">
              {t("particulieren.sub", { count: filtered.length })}
            </p>
          </div>
          <button type="button" data-testid="btn-new-group" className="btn btn-secondary"
                  onClick={startNewGroup}>
            <Plus size={16} /> {t("particulieren.new_group")}
          </button>
        </div>

        {/* Group sub-tabs */}
        <div className="flex flex-wrap gap-1 border-b border-[var(--border-soft)] mb-6 items-end" data-testid="particulieren-tabs">
          <button
            type="button"
            data-testid="group-tab-all"
            onClick={() => setGroup("all")}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px
              ${groupId === "all"
                ? "text-[var(--teal-700)] border-[var(--teal-600)]"
                : "text-[var(--ink-500)] border-transparent hover:text-[var(--ink-900)]"}`}
          >
            {t("particulieren.all_tab")}
          </button>
          {groups.map(g => (
            <div key={g.id} className="flex items-center group">
              <button
                type="button"
                data-testid={`group-tab-${g.id}`}
                onClick={() => setGroup(g.id)}
                className={`pl-4 pr-2 py-3 text-sm font-medium transition-colors border-b-2 -mb-px
                  ${groupId === g.id
                    ? "text-[var(--teal-700)] border-[var(--teal-600)]"
                    : "text-[var(--ink-500)] border-transparent hover:text-[var(--ink-900)]"}`}
              >
                {g.name}
                <span className="ml-2 text-[11px] text-[var(--ink-400)]">
                  ({g.postcodes?.length || 0})
                </span>
              </button>
              {groupId === g.id && (
                <div className="flex items-center gap-0.5 -mb-px border-b-2 border-[var(--teal-600)] pr-2 py-3">
                  <button type="button" data-testid={`group-edit-${g.id}`}
                          onClick={() => startEditGroup(g)}
                          className="p-1 rounded hover:bg-[var(--sand-100)] text-[var(--ink-500)]">
                    <Pencil size={13} />
                  </button>
                  <button type="button" data-testid={`group-delete-${g.id}`}
                          onClick={() => deleteGroup(g)}
                          className="p-1 rounded hover:bg-[var(--sand-100)] text-[var(--ink-500)] hover:text-[var(--danger)]">
                    <Trash2 size={13} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="flex flex-wrap items-center gap-2 max-w-3xl mb-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-400)]" />
            <input
              data-testid="particulieren-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`${t("common.search")}…`}
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-[var(--border-soft)] bg-white text-sm focus:border-[var(--teal-500)] outline-none"
            />
          </div>
          <div className="relative">
            <ArrowUpDown size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-400)] pointer-events-none" />
            <select
              data-testid="particulieren-sort"
              value={sortKey}
              onChange={(e) => setSort(e.target.value)}
              className="h-10 pl-9 pr-8 rounded-lg border border-[var(--border-soft)] bg-white text-sm
                         text-[var(--ink-700)] focus:border-[var(--teal-500)] outline-none cursor-pointer
                         appearance-none"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%237A8A8A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 10px center",
              }}
            >
              <option value="date_posted_desc">Nieuwste eerst (jong → oud)</option>
              <option value="date_posted_asc">Oudste eerst (oud → jong)</option>
              <option value="price_desc">Prijs: hoog → laag</option>
              <option value="price_asc">Prijs: laag → hoog</option>
            </select>
          </div>
        </div>
        <div className="mb-5">
          <LeadFilterChips value={filter} onChange={setFilter} sourceType="particulier"
                           actionFilters={[
                             { key: "succes_brief", label: "Succes brief" },
                             { key: "brief_1",      label: "Brief 1" },
                             { key: "brief_2",      label: "Brief 2" },
                             { key: "brief_3",      label: "Brief 3" },
                           ]} />
        </div>

        {/* List */}
        {loading ? (
          <div className="text-[var(--ink-500)] text-sm">{t("common.loading")}</div>
        ) : filtered.length === 0 ? (
          <div className="card text-center py-12 px-6" data-testid="particulieren-empty">
            <h3 className="font-display text-[20px]">{t("particulieren.empty_group")}</h3>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="particulieren-list">
            {filtered.map(lead => (
              <SimpleLeadRow
                key={lead.id}
                lead={lead}
                selected={sel.selected.has(lead.id)}
                onToggle={() => sel.toggleOne(lead.id)}
                onSelect={() => openLead(lead.id)}
                onCall={() => setCallLead(lead)}
                onDelete={() => sel.requestArchive([lead.id])}
              />
            ))}
          </ul>
        )}
      </div>

      <LeadBulkBar
        count={sel.selected.size}
        total={filtered.length}
        allSelected={filtered.length > 0 && filtered.every(l => sel.selected.has(l.id))}
        onToggleAll={() => sel.toggleAll(filtered.map(l => l.id))}
        onClear={sel.clear}
        onArchive={() => sel.requestArchive(Array.from(sel.selected))}
        onDelete={() => sel.requestDelete(Array.from(sel.selected))}
      />

      <BulkArchiveDialog sel={sel} />

      <ConfirmDialog
        open={sel.confirmIds !== null}
        danger
        title={(sel.confirmIds?.length || 0) > 1 ? "Leads definitief verwijderen" : "Lead definitief verwijderen"}
        message={leadDeleteMessage(sel.confirmIds)}
        confirmLabel="Definitief verwijderen"
        onConfirm={sel.doDelete}
        onCancel={sel.cancelDelete}
      />

      {callLead && (
        <QuickCallDialog lead={callLead} onClose={() => setCallLead(null)}
                         onSaved={() => { setCallLead(null); refreshLeads(groupId); }} />
      )}

      {editingGroup && (
        <div className="fixed inset-0 bg-black/30 z-40 grid place-items-center p-4" data-testid="group-dialog">
          <form onSubmit={saveGroup} className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
            <h2 className="font-display text-[22px]">
              {editingGroup === "new" ? t("particulieren.new_group") : t("particulieren.edit_group")}
            </h2>
            <label className="block">
              <div className="text-[12px] font-medium mb-1">{t("particulieren.group_name")}</div>
              <input autoFocus required data-testid="group-name"
                     value={groupForm.name}
                     onChange={(e) => setGroupForm({...groupForm, name: e.target.value})}
                     placeholder={t("particulieren.placeholder_name")}
                     className="w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] text-sm" />
            </label>
            <label className="block">
              <div className="text-[12px] font-medium mb-1">{t("particulieren.group_postcodes")}</div>
              <input data-testid="group-postcodes" value={groupForm.postcodes}
                     onChange={(e) => setGroupForm({...groupForm, postcodes: e.target.value})}
                     placeholder={t("particulieren.group_postcodes_hint")}
                     className="w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] text-sm" />
              <div className="text-[11px] text-[var(--ink-400)] mt-1">{t("particulieren.group_postcodes_hint")}</div>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEditingGroup(null)} className="btn btn-ghost">
                <X size={14} /> {t("users.cancel")}
              </button>
              <button type="submit" data-testid="btn-save-group" className="btn btn-primary">
                <Save size={14} /> {t("particulieren.save_group")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function SimpleLeadRow({ lead, onSelect, onCall, onDelete, selected, onToggle }) {
  const prog = contractProgress(lead.date_posted, lead.source_type);
  const days = daysSince(lead.date_posted);
  const thumb = lead.photos?.[0];
  return (
    <li onClick={onSelect}
        data-testid={`lead-row-${lead.id}`}
        className={`card cursor-pointer !p-4 grid grid-cols-12 gap-4 items-center transition-all
          ${selected ? "!border-[var(--teal-500)] bg-[var(--sand-50)]" : "hover:border-[var(--border-strong)]"}`}>
      <div className="col-span-1 shrink-0 flex items-center gap-2">
        <button type="button" onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
                data-testid={`lead-select-${lead.id}`}
                className="grid place-items-center w-6 h-6 text-[var(--ink-400)] hover:text-[var(--teal-700)]"
                title={selected ? "Deselecteren" : "Selecteren"}>
          {selected ? <CheckSquare size={18} className="text-[var(--teal-700)]" /> : <Square size={18} />}
        </button>
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-[var(--sand-200)] grid place-items-center">
          {thumb ? <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                 : <Home size={16} className="text-[var(--ink-400)]" />}
        </div>
      </div>
      <div className="col-span-4 min-w-0">
        <div className="text-[15px] font-semibold text-[var(--ink-900)] truncate flex items-center gap-2 flex-wrap">
          <span className="truncate">{lead.address} · {lead.postal_code} {lead.city}</span>
          <StatusChip lead={lead} />
          <LiveStatusBadge lead={lead} />
          {lead.in_option === true && (
            <span className="chip !text-[10px] !py-[1px] bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]"
                  data-testid={`in-option-chip-${lead.id}`}>
              In optie
            </span>
          )}
        </div>
        <div className="text-[12px] text-[var(--ink-500)] mt-1 flex flex-wrap gap-x-3">
          <span className="capitalize">{lead.property_category}</span>
          <span className="text-[var(--ink-300)]">·</span>
          <span>{lead.owner_name || "Particulier"}</span>
          <span className="text-[var(--ink-300)]">·</span>
          <span>Online {formatTimeSince(lead.date_posted)}</span>
        </div>
      </div>
      <div className="col-span-2 tabular">
        <div className="text-[15px] font-semibold">{formatEuro(lead.price)}</div>
        {lead.old_price > 0 && lead.old_price > (lead.price || 0) && (
          <div className="text-[12px] text-[var(--ink-400)] line-through">{formatEuro(lead.old_price)}</div>
        )}
      </div>
      <div className="col-span-3">
        <div className="timebar">
          <div className={`timebar-fill ${prog.tone === "warn" ? "warn" : prog.tone === "danger" ? "danger" : ""}`}
               style={{ width: `${prog.pct}%` }} />
        </div>
        <div className="flex justify-between mt-1.5 text-[11px] text-[var(--ink-500)] tabular">
          <span>{t("leads.time_label", { months: prog.monthsOut, total: prog.totalMonths })}</span>
          <span>{lead.call_count > 0 ? t("leads.calls_count", { n: lead.call_count }) : t("leads.not_called")}</span>
        </div>
      </div>
      <div className="col-span-2 flex justify-end items-center gap-2">
        {lead.shortlisted && <Bookmark size={16} className="text-[var(--teal-600)] fill-[var(--teal-500)]" />}
        <button type="button" onClick={(e) => { e.stopPropagation(); onCall(); }}
                data-testid={`btn-call-${lead.id}`}
                className="btn btn-ghost h-9 !px-3">
          <Phone size={14} /> {t("leads.call")}
        </button>
        <button type="button" onClick={(e) => { e.stopPropagation(); onDelete?.(); }}
                data-testid={`btn-delete-${lead.id}`}
                title="Lead verwijderen"
                className="p-2 rounded-md text-[#B43232] hover:bg-[#FBE7E7]">
          <Trash2 size={15} />
        </button>
      </div>
    </li>
  );
}
